import type { Editor } from "@tiptap/react";
import type { MarkType, ResolvedPos } from "@tiptap/pm/model";

/** Prefix https:// when the URL has no recognized scheme. */
export function normalizeHref(url: string): string {
	const trimmed = url.trim();
	if (!trimmed) {
		return trimmed;
	}
	if (/^(https?:\/\/|mailto:|tel:|ftp:)/i.test(trimmed)) {
		return trimmed;
	}
	if (trimmed.startsWith("/") || trimmed.startsWith("#")) {
		return trimmed;
	}
	return `https://${trimmed}`;
}

export interface LinkDraft {
	from: number;
	to: number;
	snapshotText: string;
	href: string;
	isEditing: boolean;
}

/** Full document range covered by the link mark at the current selection. */
export function resolveLinkRange(
	editor: Editor,
): { from: number; to: number } {
	const { selection, schema } = editor.state;
	const linkType = schema.marks.link;

	if (!linkType || !editor.isActive("link")) {
		return { from: selection.from, to: selection.to };
	}

	if (selection.from !== selection.to) {
		return { from: selection.from, to: selection.to };
	}

	const markRange = getMarkRange(selection.$from, linkType);
	return markRange ?? { from: selection.from, to: selection.to };
}

function getMarkRange($pos: ResolvedPos, type: MarkType): { from: number; to: number } | null {
	const start = $pos.parent.childAfter($pos.parentOffset);

	if (!start.node) {
		return null;
	}

	const mark = start.node.marks.find((markItem) => markItem.type === type);

	if (!mark) {
		return null;
	}

	let startIndex = $pos.index();
	let startPos = $pos.start() + start.offset;
	let endIndex = startIndex + 1;
	let endPos = startPos + start.node.nodeSize;

	while (startIndex > 0 && mark.isInSet($pos.parent.child(startIndex - 1).marks)) {
		startIndex -= 1;
		startPos -= $pos.parent.child(startIndex).nodeSize;
	}

	while (endIndex < $pos.parent.childCount && mark.isInSet($pos.parent.child(endIndex).marks)) {
		endPos += $pos.parent.child(endIndex).nodeSize;
		endIndex += 1;
	}

	return { from: startPos, to: endPos };
}

export function captureLinkDraft(editor: Editor): LinkDraft {
	const { from, to } = resolveLinkRange(editor);
	const snapshotText = editor.state.doc.textBetween(from, to);
	const isLinkActive = editor.isActive("link");
	const href = isLinkActive ? (editor.getAttributes("link").href as string | undefined) ?? "" : "";

	return {
		from,
		to,
		snapshotText,
		href,
		isEditing: Boolean(isLinkActive && href),
	};
}
